import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Brush,
    ResponsiveContainer,
    ReferenceLine,
} from 'recharts';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { AggregatedPnlTooltip } from './AggregatedPnlTooltip';
import { AggregatedPnlEmptyState } from './AggregatedPnlEmptyState';
import { AggregatedPnLTable } from './AggregatedPnLTable';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Spinner } from '@/components/ui/Spinner';
import { ChartTableToggle } from '@/components/ui/ChartTableToggle';
import { DownloadMenu } from '@/components/ui/DownloadMenu';
import {
    PrivacyAwareYAxisTick,
    PrivacyBlurFilter,
    usePrivacyBlurFilterId,
} from '@/components/charts/PrivacyAwareYAxisTick';
import { useAggregatedPnl } from '@/api/hooks/useLedgerAnalytics';
import { usePrivacy } from '@/contexts/PrivacyContext';
import { getErrorMessage } from '@/utils/errorHandler';
import { CHART_COLORS } from '@/utils/chartColors';
import { formatDateISO } from '@/utils/date';
import { TOGGLE_STYLES, TOGGLE_BUTTON_BASE } from '@/styles/toggleStyles';
import type { ColumnDefinition } from '@/lib/export';
import type { AnalyticsGranularity, AnalyticsMode, AggregatedPnlItem } from '@/api/types';

interface BrushRange {
    startIndex: number;
    endIndex: number;
}

interface AggregatedPnlChartProps {
    /** Collection IDs to filter by (empty = all) */
    collectionIds: number[];
    /** Tag IDs to filter by (empty = all) */
    tagIds: number[];
    /** Callback when filters should be reset */
    onResetFilters?: () => void;
    /** Current analytics mode (for mode toggle display) */
    mode?: AnalyticsMode;
    /** Callback when mode changes (enables mode toggle in header) */
    onModeChange?: (mode: AnalyticsMode) => void;
    /** Current granularity (month/year) - controlled by parent */
    granularity: AnalyticsGranularity;
    /** Callback when granularity changes */
    onGranularityChange: (granularity: AnalyticsGranularity) => void;
    /** Current view mode (chart/table) - controlled by parent */
    viewMode: 'chart' | 'table';
    /** Callback when view mode changes */
    onViewModeChange: (viewMode: 'chart' | 'table') => void;
    /** Callback when fetching state changes */
    onFetchingChange?: (isFetching: boolean) => void;
}

/**
 * Calculate responsive chart height based on container width.
 */
function getChartHeight(width: number): number {
    if (width < 640) return 300;
    if (width <= 1024) return 350;
    return 400;
}

/**
 * Format Y-axis values to compact notation (e.g., 10K, 1M).
 */
function formatYAxisValue(value: number, isMobile: boolean = false): string {
    const absValue = Math.abs(value);
    if (absValue >= 1_000_000) {
        const formatted = value / 1_000_000;
        if (isMobile) {
            return formatted % 1 === 0 ? `${formatted.toFixed(0)}M` : `${formatted.toFixed(1)}M`;
        }
        return `${formatted.toFixed(1)}M`;
    }
    if (absValue >= 1_000) {
        return `${(value / 1_000).toFixed(0)}K`;
    }
    return value.toFixed(0);
}

/**
 * Get Y-axis width based on container width.
 */
function getYAxisWidth(containerWidth: number): number {
    return containerWidth < 640 ? 40 : 65;
}

/**
 * Column definitions for aggregated P&L export.
 */
const AGGREGATED_PNL_EXPORT_COLUMNS: ColumnDefinition[] = [
    { key: 'periodLabel', label: 'Period', format: 'date' },
    { key: 'totalNetProfit', label: 'Net Profit', format: 'money' },
    { key: 'totalGainPercentage', label: 'Gain %', format: 'percent' },
];

/**
 * Aggregated P&L chart component for portfolio-level analytics.
 * Displays aggregated P&L across multiple ledgers with collection/tag filters.
 * Includes granularity toggle (month/year), chart/table toggle, download menu, and brush for date range selection.
 *
 * Notes:
 * - Only works for assets (P&L is not meaningful for debts)
 * - TRACKING_MODE_OTHER ledgers are excluded from aggregation
 * - Shows an info banner when some ledgers are excluded
 */
export function AggregatedPnlChart({
    collectionIds,
    tagIds,
    onResetFilters,
    mode,
    onModeChange,
    granularity,
    onGranularityChange,
    viewMode,
    onViewModeChange,
    onFetchingChange,
}: AggregatedPnlChartProps) {
    const { isPrivacyModeEnabled } = usePrivacy();
    const [brushRange, setBrushRange] = useState<BrushRange | null>(null);
    const [containerWidth, setContainerWidth] = useState<number>(800);
    const containerRef = useRef<HTMLDivElement>(null);

    const hasFilters = collectionIds.length > 0 || tagIds.length > 0;

    // Fetch aggregated P&L
    const {
        data: response,
        isLoading,
        isFetching,
        error,
        isError,
        refetch,
    } = useAggregatedPnl({
        granularity,
        collectionIds,
        tagIds,
    });

    // Measure container width
    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };

        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    // Reset brush range when granularity or filters change
    useEffect(() => {
        setBrushRange(null);
    }, [granularity, collectionIds, tagIds]);

    // Report fetching state to parent (only during refetch, not initial load)
    useEffect(() => {
        onFetchingChange?.(isFetching && !isLoading);
    }, [isFetching, isLoading, onFetchingChange]);

    const chartHeight = getChartHeight(containerWidth);
    const brushHeight = 40;
    const isMobile = containerWidth < 640;
    const yAxisWidth = getYAxisWidth(containerWidth);

    // Transform data for chart
    const chartData = useMemo(() => {
        if (!response?.items) return [];

        return response.items.map((item: AggregatedPnlItem) => ({
            ...item,
            name: item.periodLabel,
            chartValue: item.totalNetProfit,
        }));
    }, [response?.items]);

    const showBrush = chartData.length >= 4;

    // Check if all ledgers are excluded
    const allLedgersExcluded = response && response.eligibleLedgerCount === 0 && response.excludedLedgerCount > 0;
    const hasExcludedLedgers = response && response.excludedLedgerCount > 0;

    // Calculate min/max for Y-axis domain (include zero for reference line)
    const yDomain = useMemo(() => {
        if (chartData.length === 0) return [0, 0];
        const values = chartData.map((d) => d.chartValue);
        const min = Math.min(0, ...values);
        const max = Math.max(0, ...values);
        return [min, max];
    }, [chartData]);

    // Handle brush change
    const handleBrushChange = useCallback(
        (brushState: { startIndex?: number; endIndex?: number } | null) => {
            if (!brushState || brushState.startIndex === undefined || brushState.endIndex === undefined) {
                setBrushRange(null);
                return;
            }
            setBrushRange({
                startIndex: brushState.startIndex,
                endIndex: brushState.endIndex,
            });
        },
        []
    );

    // Color scheme for P&L - use same color as assets (indigo)
    const colors = CHART_COLORS.asset;

    const gradientId = 'aggregatedPnlGradient';
    const blurFilterId = usePrivacyBlurFilterId();

    // Export filename
    const exportFilename = `aggregated-pnl-${formatDateISO()}`;

    // Error state with retry
    if (isError) {
        return (
            <div className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary p-4">
                <ErrorAlert message={getErrorMessage(error)} />
                <div className="mt-4 text-center">
                    <button
                        onClick={() => refetch()}
                        className="px-4 py-2 text-sm font-medium text-accent hover:text-accent/80 transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // Empty state (only after loading completes with no data or all excluded)
    const hasNoData = !isLoading && response && (response.items.length === 0 || allLedgersExcluded);
    if (hasNoData) {
        return (
            <div
                ref={containerRef}
                className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary"
            >
                {/* Header with controls - mobile: 2 rows, desktop: single row */}
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        {/* Left group: Mode | Granularity (separator on desktop only) */}
                        <div className="flex items-center justify-between sm:justify-start gap-3">
                            {mode !== undefined && onModeChange ? (
                                <ModeToggle mode={mode} onChange={onModeChange} />
                            ) : (
                                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    P&L
                                </div>
                            )}
                            <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                                <GranularityToggle
                                    granularity={granularity}
                                    onChange={onGranularityChange}
                                />
                            </div>
                        </div>

                        {/* Right group: Chart/Table | Download (separator on desktop only) */}
                        <div className="flex items-center justify-between sm:justify-end gap-3">
                            <ChartTableToggle
                                value={viewMode}
                                onChange={onViewModeChange}
                                disabled
                            />
                            <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                                <DownloadMenu
                                    data={response?.items || []}
                                    columns={AGGREGATED_PNL_EXPORT_COLUMNS}
                                    filename={exportFilename}
                                    disabled
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <AggregatedPnlEmptyState
                    hasFilters={hasFilters}
                    onResetFilters={onResetFilters}
                    allLedgersExcluded={allLedgersExcluded ?? false}
                    excludedCount={response?.excludedLedgerCount}
                    minHeight={chartHeight}
                />
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary"
        >
            {/* Header with controls - mobile: 2 rows, desktop: single row */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    {/* Left group: Mode | Granularity (separator on desktop only) */}
                    <div className="flex items-center justify-between sm:justify-start gap-3">
                        {mode !== undefined && onModeChange ? (
                            <ModeToggle mode={mode} onChange={onModeChange} />
                        ) : (
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                P&L
                            </div>
                        )}
                        <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                            <GranularityToggle
                                granularity={granularity}
                                onChange={onGranularityChange}
                            />
                        </div>
                    </div>

                    {/* Right group: Chart/Table | Download (separator on desktop only) */}
                    <div className="flex items-center justify-between sm:justify-end gap-3">
                        <ChartTableToggle
                            value={viewMode}
                            onChange={onViewModeChange}
                        />
                        <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                            <DownloadMenu
                                data={response?.items || []}
                                columns={AGGREGATED_PNL_EXPORT_COLUMNS}
                                filename={exportFilename}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Excluded ledgers warning banner */}
            {hasExcludedLedgers && (
                <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
                    <ExclamationCircleIcon className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                        {response.excludedLedgerCount} {response.excludedLedgerCount === 1 ? 'ledger' : 'ledgers'} excluded from P&L
                        (tracked as "Other")
                    </p>
                </div>
            )}

            {/* Content area - conditionally render chart or table */}
            {viewMode === 'table' ? (
                <div className="relative">
                    {/* Loading overlay for table view */}
                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/50 dark:bg-gray-900/50">
                            <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-lg">
                                <Spinner size="sm" />
                                <span className="text-sm text-gray-600 dark:text-gray-300">Loading...</span>
                            </div>
                        </div>
                    )}
                    <AggregatedPnLTable
                        items={response?.items || []}
                        currency={response?.currency || 'USD'}
                    />
                </div>
            ) : (
                <div
                    className="relative w-full chart-container px-4 pb-4"
                    style={{ height: chartHeight + (showBrush ? brushHeight + 20 : 0) }}
                >
                    <style>{`
                        .chart-container .recharts-cartesian-grid-horizontal line,
                        .chart-container .recharts-cartesian-grid-vertical line {
                            stroke: #e5e7eb;
                        }
                        .dark .chart-container .recharts-cartesian-grid-horizontal line,
                        .dark .chart-container .recharts-cartesian-grid-vertical line {
                            stroke: #374151;
                        }
                        .chart-container .recharts-brush-slide {
                            fill: ${colors.fill};
                            fill-opacity: 0.2;
                        }
                        .dark .chart-container .recharts-brush-slide {
                            fill: ${colors.fill};
                            fill-opacity: 0.3;
                        }
                        .chart-container .recharts-brush-traveller rect {
                            fill: ${colors.stroke};
                        }
                    `}</style>
                    {/* Loading overlay */}
                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center z-10">
                            <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-lg">
                                <Spinner size="sm" />
                                <span className="text-sm text-gray-600 dark:text-gray-300">Loading...</span>
                            </div>
                        </div>
                    )}
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={chartData}
                            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                        >
                            <defs>
                                <PrivacyBlurFilter id={blurFilterId} />
                                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                    <stop
                                        offset="5%"
                                        stopColor={colors.fill}
                                        stopOpacity={0.3}
                                    />
                                    <stop
                                        offset="95%"
                                        stopColor={colors.fill}
                                        stopOpacity={0.05}
                                    />
                                </linearGradient>
                            </defs>
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                            />
                            <XAxis
                                dataKey="name"
                                tick={{ fontSize: 12 }}
                                tickLine={false}
                                axisLine={false}
                                dy={10}
                                className="text-gray-600 dark:text-gray-400"
                            />
                            <YAxis
                                key={String(isPrivacyModeEnabled)}
                                tick={
                                    <PrivacyAwareYAxisTick
                                        formatValue={(value) => formatYAxisValue(value, isMobile)}
                                        blurred={isPrivacyModeEnabled}
                                        blurFilterId={blurFilterId}
                                        fontSize={isMobile ? 10 : 12}
                                    />
                                }
                                tickLine={false}
                                axisLine={false}
                                width={yAxisWidth}
                                domain={yDomain}
                            />
                            {/* Zero reference line for P&L context */}
                            <ReferenceLine
                                y={0}
                                stroke="#9ca3af"
                                strokeDasharray="3 3"
                                strokeWidth={1}
                            />
                            {response && (
                                <Tooltip
                                    content={<AggregatedPnlTooltip currency={response.currency} />}
                                    cursor={{ stroke: colors.stroke, strokeWidth: 1, strokeDasharray: '3 3' }}
                                />
                            )}
                            <Area
                                type="monotone"
                                dataKey="chartValue"
                                stroke={colors.stroke}
                                strokeWidth={2}
                                fill={`url(#${gradientId})`}
                                animationDuration={300}
                                animationEasing="ease-out"
                            />
                            {showBrush && (
                                <Brush
                                    dataKey="name"
                                    height={brushHeight}
                                    stroke={colors.stroke}
                                    fill="transparent"
                                    onChange={handleBrushChange}
                                    startIndex={brushRange?.startIndex}
                                    endIndex={brushRange?.endIndex}
                                    travellerWidth={10}
                                >
                                    <AreaChart data={chartData}>
                                        <Area
                                            type="monotone"
                                            dataKey="chartValue"
                                            stroke={colors.stroke}
                                            fill={colors.fill}
                                            fillOpacity={0.2}
                                        />
                                    </AreaChart>
                                </Brush>
                            )}
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}

/**
 * Granularity toggle component for switching between month and year views.
 */
interface GranularityToggleProps {
    granularity: AnalyticsGranularity;
    onChange: (granularity: AnalyticsGranularity) => void;
    disabled?: boolean;
}

function GranularityToggle({ granularity, onChange, disabled = false }: GranularityToggleProps) {
    return (
        <div className={TOGGLE_STYLES.container}>
            <button
                onClick={() => onChange('month')}
                disabled={disabled}
                className={`w-10 py-1.5 text-sm font-medium ${TOGGLE_BUTTON_BASE} ${
                    granularity === 'month'
                        ? TOGGLE_STYLES.active
                        : TOGGLE_STYLES.inactive
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
                M
            </button>
            <button
                onClick={() => onChange('year')}
                disabled={disabled}
                className={`w-10 py-1.5 text-sm font-medium ${TOGGLE_BUTTON_BASE} ${
                    granularity === 'year'
                        ? TOGGLE_STYLES.active
                        : TOGGLE_STYLES.inactive
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
                Y
            </button>
        </div>
    );
}

/**
 * Mode toggle component for switching between Value, P&L, and MWRR views.
 * P&L chart is only for assets, so Value, P&L, and MWRR options are shown.
 */
interface ModeToggleProps {
    mode: AnalyticsMode;
    onChange: (mode: AnalyticsMode) => void;
}

function ModeToggle({ mode, onChange }: ModeToggleProps) {
    const options: { value: AnalyticsMode; label: string }[] = [
        { value: 'value', label: 'Value' },
        { value: 'pnl', label: 'P&L' },
        { value: 'mwrr', label: 'MWRR' },
    ];

    return (
        <div className={TOGGLE_STYLES.container}>
            {options.map((option) => (
                <button
                    key={option.value}
                    onClick={() => onChange(option.value)}
                    className={`px-4 py-1.5 text-sm font-medium ${TOGGLE_BUTTON_BASE} ${
                        mode === option.value
                            ? TOGGLE_STYLES.active
                            : TOGGLE_STYLES.inactive
                    }`}
                >
                    {option.label}
                </button>
            ))}
        </div>
    );
}
